/**
 * お題タブの純粋な計算（画面に依存しない）。単体テストは tests/unit/prompts。
 * 得点・コンプ・交換の成否はサーバーが決める。ここは表示の並び・数え方・見せ方の判定だけ。
 */
import type { GameState, OpenListing, RpcErrorCode, StateArea, StateHandCard, StatePrompt } from '../../api/types';
import { fmtRemaining } from '../../core/clock';

// ---------------------------------------------------------------- エリアの色

export type Paint = 'red' | 'amber' | 'blue' | 'green';
export const PAINTS: Paint[] = ['red', 'amber', 'blue', 'green'];
/** tokens.css の --area-* と同じ */
export const PAINT_HEX: Record<Paint, string> = { red: '#a22c3f', amber: '#985c14', blue: '#23487d', green: '#3d602b' };

/**
 * エリアの色を切手の4色のどれかにする。areas.color は色名（red/amber/blue/green）か #rrggbb を想定し、
 * 空・不明なら並び順で割り当てる（Claude判断。実データの形が未確認のため）
 */
export function paintOf(color: string | null | undefined, index: number): Paint {
  const c = (color ?? '').trim().toLowerCase();
  if ((PAINTS as string[]).includes(c)) return c as Paint;
  const m = /^#?([0-9a-f]{6})$/.exec(c);
  if (m && m[1]) {
    const v = parseInt(m[1], 16);
    const rgb = [(v >> 16) & 255, (v >> 8) & 255, v & 255];
    let best: Paint = 'red';
    let bd = Infinity;
    for (const p of PAINTS) {
      const pv = parseInt(PAINT_HEX[p].slice(1), 16);
      const d = ((pv >> 16) & 255) - (rgb[0] ?? 0);
      const e = ((pv >> 8) & 255) - (rgb[1] ?? 0);
      const f = (pv & 255) - (rgb[2] ?? 0);
      const dist = d * d + e * e + f * f;
      if (dist < bd) {
        bd = dist;
        best = p;
      }
    }
    return best;
  }
  return PAINTS[((index % 4) + 4) % 4] ?? 'red';
}

// ---------------------------------------------------------------- いまいるエリア

export function distanceM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLon = (lon2 - lon1) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** 表示用の「いまいるエリア」の近さ（m）。サーバーの判定ではない（Claude判断） */
export const HERE_BASE_M = 80;

/**
 * 位置から一番近いスポット・場所固定のお題を探し、80m＋精度の中にあればそのエリアを返す。
 * サーバー側の最寄りエリア判定とは別の、表示だけの簡易判定
 */
export function hereAreaId(state: GameState, fix: { lat: number; lon: number; acc: number } | null): string | null {
  if (!fix) return null;
  const limit = HERE_BASE_M + Math.max(0, fix.acc);
  let best: { id: string; d: number } | null = null;
  const consider = (areaId: string, lat: number | null, lon: number | null): void => {
    if (lat === null || lon === null) return;
    const d = distanceM(fix.lat, fix.lon, lat, lon);
    if (d <= limit && (!best || d < best.d)) best = { id: areaId, d };
  };
  for (const s of state.spots) consider(s.area_id, s.latitude, s.longitude);
  for (const p of state.prompts) if (p.fixed_location) consider(p.area_id, p.latitude, p.longitude);
  return (best as { id: string } | null)?.id ?? null;
}

// ---------------------------------------------------------------- 束（エリア）

export interface Bundle {
  area: StateArea;
  paint: Paint;
  /** 色の16進（インライン style 用） */
  hex: string;
  photos: StatePrompt[];
  quizzes: StatePrompt[];
  /** そろった数 / 全部の数（サーバーの prompts_held / prompts_total） */
  held: number;
  total: number;
  photoHeld: number;
  quizHeld: number;
  points: number;
  isTarget: boolean;
  complete: boolean;
  here: boolean;
}

/** 束の並び。いまいるエリアを先頭（左上）に、あとは areas の並びのまま */
export function buildBundles(state: GameState, hereId: string | null): Bundle[] {
  const list = state.areas.map((area, i): Bundle => {
    const ps = state.prompts.filter((p) => p.area_id === area.area_id);
    const photos = ps.filter((p) => p.prompt_type === 'photo');
    const quizzes = ps.filter((p) => p.prompt_type === 'quiz');
    const paint = paintOf(area.color, i);
    return {
      area,
      paint,
      hex: PAINT_HEX[paint],
      photos,
      quizzes,
      held: area.prompts_held,
      total: area.prompts_total,
      photoHeld: photos.filter((p) => p.held !== null).length,
      quizHeld: quizzes.filter((p) => p.held !== null).length,
      points: area.complete_points,
      isTarget: area.is_target,
      complete: area.completed_at !== null,
      here: area.area_id === hereId,
    };
  });
  const here = list.filter((b) => b.here);
  return [...here, ...list.filter((b) => !b.here)];
}

/** 前の状態から次の状態でコンプになったエリア（最初の取得は対象にしない） */
export function completedAreas(prev: GameState | null, next: GameState): string[] {
  if (!prev) return [];
  const before = new Map(prev.areas.map((a) => [a.area_id, a.completed_at !== null]));
  return next.areas.filter((a) => a.completed_at !== null && before.get(a.area_id) === false).map((a) => a.area_id);
}

/** 前の状態から次の状態で手に入ったお題（撮った・答えた・交換でもらった） */
export function newlyHeld(prev: GameState | null, next: GameState): StatePrompt[] {
  if (!prev) return [];
  const before = new Map(prev.prompts.map((p) => [p.prompt_id, p.held !== null]));
  return next.prompts.filter((p) => p.held !== null && before.get(p.prompt_id) === false);
}

/** 交換で届いた写し（手元に新しく入った trade のカード） */
export function receivedCopies(prev: GameState | null, next: GameState): StateHandCard[] {
  if (!prev) return [];
  const before = new Set(prev.hand.map((h) => h.prompt_id));
  return next.hand.filter((h) => h.acquired_via === 'trade' && !before.has(h.prompt_id));
}

/** 「500点 獲得」「800点」の点の行 */
export function pointsText(b: Pick<Bundle, 'points' | 'complete'>): string {
  return b.complete ? `${b.points}点 獲得` : `${b.points}点`;
}

// ---------------------------------------------------------------- クイズ

/** クイズに答えられるまでの残りがあるか */
export function isWaiting(ms: number): boolean {
  return ms > 0;
}

/** 「m:ss 後に答えられます」（README ㊵。待ちはチームの全クイズ共通） */
export function quizWaitText(ms: number): string {
  return `${fmtRemaining(ms)} 後に答えられます`;
}

// ---------------------------------------------------------------- 交換所

export type ListingKind = 'can' | 'lack' | 'have';

export interface PairView {
  listing: OpenListing;
  kind: ListingKind;
  /** もらえる絵葉書（掲示された絵葉書）を自分がもう持っているか */
  giveHeld: boolean;
  /** 渡す側（ほしいもの）を自分が出せるか。切手つきで表示する */
  wantOwned: boolean;
  /** 渡せる写真の候補（交換できるとき） */
  candidates: StateHandCard[];
  /** 撮れない1枚を出している掲示 */
  hot: boolean;
  /** エリアのどれでも */
  any: boolean;
}

/** 掲示の「ほしい」に合う、渡せる写真（自分で撮った写真で、掲示中・申し出中でない） */
export function offerCandidates(l: Pick<OpenListing, 'want_prompt_id' | 'want_area_id'>, hand: StateHandCard[]): StateHandCard[] {
  return hand.filter(
    (h) =>
      h.tradable &&
      h.prompt_type === 'photo' &&
      (l.want_prompt_id !== null ? h.prompt_id === l.want_prompt_id : h.area_id === l.want_area_id),
  );
}

/** 掲示1件の見せ方。札「交換できます」＝出されている絵葉書を持っておらず、ほしい絵葉書を渡せるとき（README ㊶） */
export function classifyListing(l: OpenListing, state: GameState): PairView {
  const heldIds = new Set(state.prompts.filter((p) => p.held !== null).map((p) => p.prompt_id));
  const giveHeld = heldIds.has(l.prompt_id);
  const candidates = offerCandidates(l, state.hand);
  const any = l.want_prompt_id === null;
  const wantOwned = any ? candidates.length > 0 : heldIds.has(l.want_prompt_id as number);
  const kind: ListingKind = giveHeld ? 'have' : candidates.length > 0 ? 'can' : 'lack';
  return {
    listing: l,
    kind,
    giveHeld,
    wantOwned,
    candidates,
    hot: state.target?.blocked_prompt_id === l.prompt_id,
    any,
  };
}

const ORDER: Record<ListingKind, number> = { can: 0, lack: 1, have: 2 };

/**
 * 並び＝交換できる → 持っていない → 持っている。同じ組の中は新しい順。
 * 撮れない1枚かどうかは並びに使わない（README ㊶ 本人10/31）
 */
export function sortListings(listings: OpenListing[], state: GameState): PairView[] {
  return listings
    .map((l) => classifyListing(l, state))
    .sort((a, b) => {
      const k = ORDER[a.kind] - ORDER[b.kind];
      if (k !== 0) return k;
      const t = Date.parse(b.listing.created_at) - Date.parse(a.listing.created_at);
      if (t !== 0 && !Number.isNaN(t)) return t;
      return b.listing.listing_id - a.listing.listing_id;
    });
}

/** 交換できる掲示の数（交換所の入口「交換できる n件」） */
export function tradableCount(state: GameState): number {
  return state.exchange.listings.filter((l) => classifyListing(l, state).kind === 'can').length;
}

export interface WantRow {
  area: StateArea;
  paint: Paint;
  /** まだ持っていない写真のお題 */
  prompts: StatePrompt[];
}

/** 掲示する：もらうの選択肢（エリアごとの、持っていない写真のお題と「〇〇のどれか」） */
export function wantRows(state: GameState): WantRow[] {
  return state.areas
    .map((area, i) => ({
      area,
      paint: paintOf(area.color, i),
      prompts: state.prompts.filter((p) => p.area_id === area.area_id && p.prompt_type === 'photo' && p.held === null),
    }))
    .filter((r) => r.prompts.length > 0);
}

/** 掲示する：渡すの選択肢（撮った写真で、掲示中でないもの） */
export function giveChoices(state: GameState): StateHandCard[] {
  return state.hand.filter((h) => h.tradable && h.prompt_type === 'photo');
}

/** 掲示する：最初から選んでおくもらう（撮れない1枚。まだ持っていなければ） */
export function defaultWant(state: GameState): number | null {
  const id = state.target?.blocked_prompt_id ?? null;
  if (id === null) return null;
  const p = state.prompts.find((x) => x.prompt_id === id);
  return p && p.held === null ? id : null;
}

export interface Want {
  promptId?: number;
  areaId?: string;
}

/** 掲示するを押せるか：もらうと渡すの両方を選んだとき */
export function canPost(give: number | null, want: Want | null): boolean {
  return give !== null && want !== null && (want.promptId !== undefined) !== (want.areaId !== undefined);
}

/** 次に掲示できるまでの残り（ms）。掲示歴なし・過ぎていれば 0 */
export function listingWaitMs(nextListingAt: string | null, now: number): number {
  if (!nextListingAt) return 0;
  return Math.max(0, Date.parse(nextListingAt) - now);
}

// ---------------------------------------------------------------- エラーの短い文

const ERROR_TEXT: Partial<Record<RpcErrorCode | 'network' | 'unknown', string>> = {
  network: '通信できませんでした。もう一度押してください。',
  prompt_blocked: 'このお題は撮れません。交換所でもらえます。',
  already_have: 'このお題はもう持っています。',
  quiz_cooldown: 'クイズはまだ答えられません。',
  prompts_locked: 'お題はまだ解禁されていません。',
  outside_area: 'お題のエリアの外にいると判定されました。エリアの中で答えてください。',
  invalid_choice: '選べない答えです。',
  prompt_not_quiz: 'このお題はクイズではありません。',
  prompt_is_quiz: 'このお題は写真では出せません。',
  listing_cooldown: 'まだ掲示できません。',
  listing_not_found: 'この掲示はもう終わりました。',
  offer_does_not_match: 'その写真では交換できません。',
  duplicate_card: '同じ写真が2枚になるので交換できません。',
  card_not_available: 'この写真は今は出せません。',
  invalid_want: '選べないお題です。',
  want_required: 'もらうお題を選んでください。',
  event_not_running: '開催中ではないため答えられません。始まってからお試しください。',
  event_ended: 'ゲームは終了しました。',
  invalid_session: 'ログインし直してください。',
  terms_not_accepted: '同意が済んでいません。',
};

export function errorText(code: string): string {
  // 理由の分からないものはコードを添える（運営に伝えられるように）
  return (ERROR_TEXT as Record<string, string | undefined>)[code] ?? `うまくいきませんでした。もう一度お試しください。（${code}）`;
}

// ---------------------------------------------------------------- 絵葉書の傾き

/** 切手・消印の傾き（README「絵葉書の傾き」）。同じお題はいつも同じ傾き */
export function tilt(id: number): { stamp: number; pm: number } {
  const stamps = [-2.5, 2.5, 0];
  const pms = [-6, -6, 3];
  const k = Math.abs(id) % 3;
  return { stamp: stamps[k] ?? 0, pm: pms[k] ?? -6 };
}

// ---------------------------------------------------------------- エリアの引き当て

export interface AreaInfo {
  id: string;
  name: string;
  paint: Paint;
  hex: string;
}

/** area_id から名前と色を引く */
export function areaMap(state: GameState): Map<string, AreaInfo> {
  const m = new Map<string, AreaInfo>();
  state.areas.forEach((a, i) => {
    const paint = paintOf(a.color, i);
    m.set(a.area_id, { id: a.area_id, name: a.name, paint, hex: PAINT_HEX[paint] });
  });
  return m;
}

/** 掲示の「ほしい」「出す」に出す題名。エリアのどれでもは「本館のどれか」 */
export function anyTitle(areaName: string): string {
  return `${areaName}のどれか`;
}

/** 状態のうち画面に関わる部分の署名。同じなら描き直さない（動きを途中で止めないため） */
export function viewSignature(s: GameState): string {
  return JSON.stringify([
    s.areas.map((a) => [a.area_id, a.prompts_held, a.prompts_total, a.completed_at, a.is_target, a.complete_points, a.color]),
    s.prompts.map((p) => [p.prompt_id, p.held, p.blocked, p.retry_at]),
    s.hand.map((h) => [h.prompt_id, h.acquired_via, h.reserved, h.tradable]),
    s.exchange,
    s.target?.blocked_prompt_id ?? null,
  ]);
}
